import { useQuery } from "@tanstack/react-query";
import { useRef } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router";

import type { SelfDto, SelfUpdateRequest } from "@/api/types";
import { AutoTranslationLanguageSelector } from "@/components/AutoTranslation/AutoTranslationLanguageSelector";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { ConfirmModal } from "@/components/ConfirmModal/ConfirmModal";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { Form } from "@/components/Form/Form";
import { FormImageInput } from "@/components/Form/FormImageInput";
import { FormInput } from "@/components/Form/FormInput";
import { FormRadioCard, FormRadioCardGroup } from "@/components/Form/FormRadioCard";
import { FormTextArea } from "@/components/Form/FormTextArea";
import type { FormImage } from "@/components/ImageInput/useImageInput";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { Button } from "@/design-system/Button";
import { FormField, FormFieldControl } from "@/design-system/FormField";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { toUiLanguageId } from "@/helpers/languages";
import { createImageSizeRule, createImageTypeRule, createRequiredStringRule } from "@/helpers/rules";
import { isValidPhoneNumber } from "@/helpers/validation";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useUploadImage } from "@/hooks/Network/useUploadImage";
import { useBool } from "@/hooks/useBool";
import { useScrollToHash } from "@/hooks/useScrollToHash";
import { useGeneralQueries } from "@/queries/general/queryOptions";
import { selfAccountMutations } from "@/queries/self-account/mutations";
import type { Languages } from "@/translations";
import { useTranslation } from "@/translations";

import { DeleteAccountSection } from "./DeleteAccountSection";

const MAX_CHARACTERS = 255;

interface ResidentEditSelfFormValues {
  avatar: FormImage[];
  firstName: string;
  lastName: string;
  mobileNumber: string;
  email: string;
  occupation?: string;
  biography: string;
  chatPermission: SelfDto["chatPermission"];
  isAutoTranslationEnabled?: boolean;
  autoTranslationLanguageId?: Languages;
}

export function ResidentEditSelfPage(): React.ReactNode {
  const [isCancelModalOpen, cancelModalHandler] = useBool(false);

  const { t } = useTranslation();
  const sessionUser = useSessionUser();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const form = useForm<ResidentEditSelfFormValues>({
    values: {
      firstName: sessionUser.firstName,
      lastName: sessionUser.lastName,
      mobileNumber: sessionUser.phoneNumber || "",
      email: sessionUser.email,
      chatPermission: sessionUser.chatPermission,
      occupation: sessionUser.occupation,
      avatar: sessionUser.avatar ? [sessionUser.avatar] : [],
      biography: sessionUser.biography || "",
      autoTranslationLanguageId: sessionUser.autoTranslationLanguage?.id,
      isAutoTranslationEnabled: Boolean(sessionUser.autoTranslationLanguage),
    },
  });
  const currAutoTranslationLanguageId = useWatch({
    name: "autoTranslationLanguageId",
    control: form.control,
  });

  const { uploadFormImage, isUploadingImage } = useUploadImage();
  const showFlashToast = useFlashToast();
  const updateSelfMutation = selfAccountMutations.useUpdateSelf();
  useScrollToHash(Boolean(sessionUser));

  const generalQueries = useGeneralQueries();
  const {
    data: autoTranslationLanguages,
    isPending: isPendingAutoTranslationLanguages,
    isError: isErrorAutoTranslationLanguages,
    error: errorAutoTranslationLanguages,
  } = useQuery(generalQueries.languages({ subset: "autoTranslation" }));

  if (isPendingAutoTranslationLanguages) {
    return <FullSizeLoader />;
  }

  if (isErrorAutoTranslationLanguages) {
    return <ErrorPage error={errorAutoTranslationLanguages} />;
  }

  async function handleSubmit(formValues: ResidentEditSelfFormValues) {
    const payload: SelfUpdateRequest = {
      uiLanguageId: toUiLanguageId(sessionUser.language.id),
      autoTranslationLanguageId: formValues.autoTranslationLanguageId,
      hideEmail: sessionUser.emailHidden,
      hidePhoneNumber: sessionUser.phoneNumberHidden,
      hideHouseNumber: sessionUser.hideHouseNumber,
      enableChat: formValues.chatPermission === "enabled",
      firstName: formValues.firstName,
      lastName: formValues.lastName,
      biography: formValues.biography,
      phoneNumber: formValues.mobileNumber,
      occupation: formValues.occupation,
    };

    if (formValues.avatar?.[0]) {
      const avatarImage = await uploadFormImage(formValues.avatar?.[0]);

      payload.avatarId = avatarImage?.id;
    }

    updateSelfMutation.mutate(payload, {
      onError() {
        showFlashToast({ message: t("page.self-edit.submit.error"), variant: "alert" });
      },
      onSuccess() {
        showFlashToast({ message: t("page.self-edit.submit.success"), variant: "success" });
        goBack();
      },
    });
  }

  function goBack() {
    cancelModalHandler.setFalse();
    void navigate(-1);
  }

  const project = sessionUser.project;
  const currAutoTranslationLanguage =
    autoTranslationLanguages.find((language) => language.id === currAutoTranslationLanguageId) ?? null;

  return (
    <DocumentPage
      containerBoundary="inherit"
      contentSize="constrained"
      tabTitle={t("page.self-edit.title")}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name: project.name,
            },
            {
              name: t("page.self-edit.title"),
            },
          ]}
        />
      }
    >
      <Form.Root formMethods={form} onSubmit={handleSubmit}>
        <Form.Container>
          <Form.Body>
            <FormField label={t("page.self-edit.form.avatar.label")}>
              <FormImageInput<ResidentEditSelfFormValues, "avatar">
                ref={fileInputRef}
                name="avatar"
                rules={{
                  validate: {
                    size: createImageSizeRule({ t }),
                    type: createImageTypeRule({ t }),
                  },
                }}
                isAvatarInput
                cropper
                canDelete={false}
              />
            </FormField>
            <FormField label={t("page.self-edit.form.first-name.label")} required>
              <FormInput<ResidentEditSelfFormValues>
                name="firstName"
                placeholder={t("page.self-edit.form.first-name.placeholder")}
                rules={{
                  validate: {
                    required: createRequiredStringRule(t, "page.self-edit.form.first-name.label"),
                  },
                  maxLength: {
                    message: t("components.form.error.max-length", { length: MAX_CHARACTERS }),
                    value: MAX_CHARACTERS,
                  },
                }}
              />
            </FormField>
            <FormField label={t("page.self-edit.form.last-name.label")} required>
              <FormInput<ResidentEditSelfFormValues>
                name="lastName"
                placeholder={t("page.self-edit.form.last-name.placeholder")}
                rules={{
                  validate: {
                    required: createRequiredStringRule(t, "page.self-edit.form.last-name.label"),
                  },
                  maxLength: {
                    message: t("components.form.error.max-length", { length: MAX_CHARACTERS }),
                    value: MAX_CHARACTERS,
                  },
                }}
              />
            </FormField>
            <FormField label={t("page.self-edit.form.biography.label")}>
              <FormTextArea<ResidentEditSelfFormValues>
                name="biography"
                placeholder={t("page.self-edit.form.biography.placeholder")}
              />
            </FormField>
            {!sessionUser.company && (
              <FormField label={t("page.self-edit.form.address")} description={t("page.self-edit.form.address.note")}>
                <p>{sessionUser.address?.location ?? "-"}</p>
              </FormField>
            )}
            {sessionUser.company && (
              <FormField label={t("page.self-edit.form.company")} description={t("page.self-edit.form.company.note")}>
                <p>{sessionUser.company?.name}</p>
              </FormField>
            )}
            {project.type === "companyBased" && (
              <FormField label={t("page.self-edit.form.occupation.label")}>
                <FormInput<ResidentEditSelfFormValues, "occupation">
                  name="occupation"
                  placeholder={t("page.self-edit.form.occupation.placeholder")}
                  type="occupation"
                  rules={{
                    maxLength: {
                      message: t("components.form.error.max-length", { length: MAX_CHARACTERS }),
                      value: MAX_CHARACTERS,
                    },
                  }}
                />
              </FormField>
            )}

            <FormField label={t("page.self-edit.form.mobile.label")} description={t("page.self-edit.form.mobile.note")}>
              <FormInput<ResidentEditSelfFormValues, "mobileNumber">
                name="mobileNumber"
                placeholder={t("page.self-edit.form.mobile.placeholder")}
                type="tel"
                inputMode="tel"
                rules={{
                  validate: {
                    isValid(value) {
                      value = value?.trim();

                      if (!value) {
                        return;
                      }

                      return isValidPhoneNumber(value) ? undefined : t("components.form.error.invalid-phone-number");
                    },
                  },
                }}
              />
            </FormField>
            <FormField
              label={t("page.self-edit.form.email.label")}
              description={t("page.self-edit.form.email.note")}
              required
            >
              <FormInput<ResidentEditSelfFormValues, "email">
                name="email"
                placeholder={t("page.self-edit.form.email.placeholder")}
                type="email"
                inputMode="email"
                disabled
              />
            </FormField>
            <FormField id="chat-settings" label={t("page.self-edit.form.chat-permission.label")}>
              <FormRadioCardGroup>
                <FormRadioCard<ResidentEditSelfFormValues>
                  label={t("page.self-edit.form.chat-permission.option.enabled")}
                  description={t("page.self-edit.form.chat-permission.option.enabled.description")}
                  name="chatPermission"
                  value="enabled"
                />
                <FormRadioCard<ResidentEditSelfFormValues>
                  label={t("page.self-edit.form.chat-permission.option.disabled")}
                  description={t("page.self-edit.form.chat-permission.option.disabled.description")}
                  name="chatPermission"
                  value="disabled"
                />
              </FormRadioCardGroup>
            </FormField>
            <FormField label={t("page.self-edit.form.auto-translation-language.label")}>
              <FormFieldControl data-testid="auto-translation-language-selector">
                <AutoTranslationLanguageSelector
                  languages={autoTranslationLanguages}
                  selectedLanguage={currAutoTranslationLanguage}
                  onSelectLanguage={(language) =>
                    form.setValue("autoTranslationLanguageId", language?.id, {
                      shouldDirty: true,
                    })
                  }
                />
              </FormFieldControl>
            </FormField>
            <DeleteAccountSection />
          </Form.Body>
          <Form.Footer
            renderPrimaryAction={() => (
              <Button
                type="submit"
                isLoading={updateSelfMutation.isPending || isUploadingImage}
                data-testid="self-edit-submit-btn"
              >
                {t("page.self-edit.form.submit")}
              </Button>
            )}
            renderSecondaryAction={() => (
              <Button
                data-testid="self-edit-cancel-btn"
                variant="subtle"
                tone="error"
                onClick={() => {
                  if (Object.keys(form.formState.dirtyFields).length > 0) {
                    cancelModalHandler.setTrue();
                  } else {
                    goBack();
                  }
                }}
              >
                {t("common.action.cancel")}
              </Button>
            )}
          />
        </Form.Container>
      </Form.Root>
      <ConfirmModal
        tone="primary"
        title={t("page.self-edit.cancel-modal.title")}
        description={t("page.self-edit.cancel-modal.description")}
        isLoading={false}
        onReject={cancelModalHandler.setFalse}
        onOpenChange={cancelModalHandler.set}
        rejectBtnProps={{
          "data-testid": "cancel-modal-cancel",
        }}
        onResolve={goBack}
        resolveBtnProps={{
          text: t("common.action.ok"),
          "data-testid": "cancel-modal-confirm",
        }}
        isOpened={isCancelModalOpen}
        shouldCloseOnEsc
        data-testid="cancel-modal"
      />
    </DocumentPage>
  );
}
